import React, { PureComponent, PropTypes } from 'react';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import classnames from 'classnames';
import { cache, compareCards, compareFlatDecks, dataHasValues, dataIsHiddenOrDeleted, debounce, dehydrateCard, duplicateData, flattenDeck, makeEmptyDeck, makeEmptyRow, purgeDeadCards, rebuildCardFromRow } from '../../services/Utils';
import { getDeckById, saveDeck, suggestLanguage } from '../../services/APIAgent';
import Mediator from '../../services/Mediator';
import { STATUS_MASTERED } from '../../services/Card';
import MainDetails from './maindetails';
import Grid from './grid';
import CardPreview from '../cardpreview';
import Importer from '../importer';
import './deckeditor.css';

const dbSuggestLanguage = cache(debounce(suggestLanguage, 500), 30);

class DeckEditor extends PureComponent {
    constructor(props, ...args) {
        super(props, ...args);
        this.state = {
            data: [],
            dataComplete: false,
            description: null,
            importerVisible: false,
            isPublic: false,
            languageSuggestions: [],
            loading: false,
            name: null,
            targetLanguage: ''
        };
        this.deck = null;
        this.onImport = this.onImport.bind(this);
        this.onDetailChange = this.onDetailChange.bind(this);
        this.onDeckVisibilityChange = this.onDeckVisibilityChange.bind(this);
        this.onCellChange = this.onCellChange.bind(this);
        this.onLanguageChange = this.onLanguageChange.bind(this);
        this.onStateChange = this.onStateChange.bind(this);
        this.resetDeck = this.resetDeck.bind(this);
        this.saveDeck = this.saveDeck.bind(this);
        this.showImport = this.showImport.bind(this);
        this.importNewCards = this.importNewCards.bind(this);
    }
    componentDidMount() {
        if (this.props.deckId === 'new')  this.createEmptyDeck();
        else this.loadDeckFromRemote();
    }
    createEmptyDeck() {
        this.deck = makeEmptyDeck();

        this.originalCopy = flattenDeck(this.deck);
        let { data, description, isPublic, name, targetLanguage } = duplicateData(this.originalCopy);

        this.setState({
            data,
            description,
            isPublic,
            loading: false,
            name,
            targetLanguage
        });
        this.testCells();
    }
    loadDeckFromRemote() {
        this.setState({
            dataComplete: true,
            loading: true
        });

        getDeckById(this.props.deckId)
        .then(deck => {
            this.deck = Object.assign({}, deck, { cards: purgeDeadCards(deck.cards) });

            this.originalCopy = flattenDeck(this.deck);
            let { data, description, isPublic, name, targetLanguage } = duplicateData(this.originalCopy);

            this.setState({
                data,
                description,
                isPublic,
                loading: false,
                name,
                targetLanguage
            });

            this.testCells();
        });
    }
    onDetailChange({ target }) {
        let { name, value } = target;

        this.setState({ [name]: value });
    }
    onLanguageChange(targetLanguage, requiresUpdate = true) {
        let { language } = this.props;
        let languageSuggestions = [];
        this.setState({ targetLanguage, languageSuggestions });

        if (requiresUpdate) {
            dbSuggestLanguage(language, targetLanguage)
            .then(languageSuggestions => this.setState({ languageSuggestions }));
        }
    }
    onDeckVisibilityChange() {
        let { isPublic } = this.state;
        this.setState({ isPublic: !isPublic });
    }
    testCells() {
        clearTimeout(this.testTimeout);
        this.testTimeout = setTimeout(() => {
            let dataComplete = this.state.data.every(([ ,state,, ...values ]) => state > STATUS_MASTERED || values.join('').trim() === '' || (values[0].trim() !== '' && values[2].trim() !== ''));
            this.setState({ dataComplete });
        }, 200);
    }
    onCellChange({ target }) {
        let { name, value } = target;

        let [ targetId, index ] = name.split(/_+/).map(v => parseInt(v, 10)).filter((v,i) => i > 0);

        let data = this.state.data.map(row => {
            let [ id, state, card, ...values ] = row;

            if (id === targetId) {
                values[index] = value;
                return [id, state, card, ...values];
            }

            return row;
        });

        let lastRow = data[data.length - 1] || null;
        if (lastRow[0] === targetId) data.push(makeEmptyRow());

        this.setState({data});
        this.testCells();
    }
    onStateChange(id, state) {
        let data = this.state.data.map(row => {
            let [rowId,, card, ...values] = row;

            if (rowId === id) return [id, state, card, ...values];
            return row;
        });

        this.setState({ data });
    }
    onImport() {
        this.setState({ importerVisible: true });
    }
    importNewCards(data) {
        let cards = data.map(row => rebuildCardFromRow(row));
        let cardCount = cards.length;
        this.deck = Object.assign({}, this.deck, { cards, cardCount });

        data.push(makeEmptyRow());

        this.setState({
            data,
            importerVisible: false
        });
        this.testCells();
    }
    resetDeck() {
        let { data, description, isPublic, name, targetLanguage } = this.originalCopy;
        this.setState({ data, description, isPublic, name, targetLanguage });
        this.testCells();
    }
    saveDeck() {
        let { data, name, description, isPublic, targetLanguage } = this.state;

        let cards = data
        .filter(dataIsHiddenOrDeleted)
        .filter(dataHasValues)
        .map(row => {
            let [ ,, card ] = row;
            let update = rebuildCardFromRow(row);

            if (compareCards(update, card)) return card;
            return update;
        })
        .map(dehydrateCard);

        let deck = Object.assign({}, this.deck, { name, description, isPublic, targetLanguage, cards, cardCount: cards.length });

        return saveDeck(deck)
        .then(({ id }) => {
            let { deckId } = this.props;
            if (id !== deckId) return Mediator.broadcast('ChangeDeckId', id);
            return this.loadDeckFromRemote();
        });
    }
    showImport(importerVisible = true) {
        this.setState({ importerVisible });
    }

    render() {
        let { data, dataComplete, description, importerVisible, isPublic, languageSuggestions, loading, name, targetLanguage } = this.state;
        let canSave = this.deck && !(compareFlatDecks(this.originalCopy, this.state)) && dataComplete;
        let mainProps = { canSave, data, dataComplete, description, languageSuggestions, isPublic, name, targetLanguage };
        let classes = classnames({
            'deck-editor': true,
            loading
        });

        return (<div className={ classes }>
            <MainDetails
                { ...mainProps }
                onDeckVisibilityChange={ this.onDeckVisibilityChange }
                onDeckDetailChange={ this.onDetailChange }
                onLanguageChange={ this.onLanguageChange }
                onResetDeck={ this.resetDeck }
                onSaveDeck={ this.saveDeck }
            />
            <TransitionGroup component="div" transitionAppearTimeout={300} transitionLeaveTimeout={300}>
                { importerVisible ? <Importer key={1} onCancel={ () => this.showImport(false) } onComplete={ this.importNewCards } /> : null }
                { data && !importerVisible ? <Grid
                    data={ data }
                    key={2}
                    onStateChange={ this.onStateChange }
                    onCellChange={ this.onCellChange }
                    onImport={ this.onImport }
                /> : null }
            </TransitionGroup>
            <CardPreview />
        </div>);
    }
}

DeckEditor.propTypes = {
    deckId: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired
};

export default DeckEditor;


// WEBPACK FOOTER //
// ./src/components/deckeditor/index.js